import Error from "@mui/icons-material/Error";
import { Box, Typography, useTheme } from "@mui/material";
import {
  federalWasteGeneratorStatusToLabel,
  stateWasteGeneratorStatusToLabel,
} from "encamp-shared/src/waste";
import {
  RcraInfoFederalWasteGeneratorStatus,
  StateRegulatoryStatus,
  WasteShipmentChartsQuery,
} from "generated-graphql/graphql";
import { ReactNode, useMemo } from "react";

import {
  GeneratorStatusKind,
  getFederalGeneratorStatusColorSettings,
} from "../../getFederalGeneratorStatusColorSettings";

/**
 * Width of the status-bar row label column. The chart in index.tsx reserves this
 * same left offset (BarChart margin.left + yAxis width, and the hit-test
 * leftAxisOffset) so the status-bar month cells line up under the chart columns.
 */
export const STATUS_BAR_LABEL_WIDTH = 90;

/**
 * Map a state status to the federal tier whose color it borrows, so the state
 * bars use the same palette as the federal ones (cyan = LQG-tier, yellow =
 * SQG-tier, deep purple = VSQG-tier). The cell label still shows the true state
 * status; only the color is mapped.
 */
const stateColorEquivalent = (
  status: StateRegulatoryStatus | null
): RcraInfoFederalWasteGeneratorStatus | null => {
  switch (status) {
    case StateRegulatoryStatus.LargeQuantityGenerator:
    case StateRegulatoryStatus.MarylandGeneratorFullyRegulated:
      return RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator;
    case StateRegulatoryStatus.MediumQuantityGenerator:
    case StateRegulatoryStatus.SmallQuantityGenerator:
    case StateRegulatoryStatus.KansasSmallQuantityGenerator:
    case StateRegulatoryStatus.TexasClass_1OnlyGenerator:
      return RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator;
    case StateRegulatoryStatus.VerySmallQuantityGenerator:
    case StateRegulatoryStatus.ConditionallyExemptSmallQuantityGenerator:
    case StateRegulatoryStatus.MiniQuantityGenerator:
      return RcraInfoFederalWasteGeneratorStatus.VerySmallQuantityGenerator;
    default:
      return null;
  }
};

/** Caution marker shown on a calculated cell when the month is an exceedance. */
const ExceedanceMarker = () => (
  <Error
    color="secondary"
    fontSize="small"
    titleAccess="Calculated status exceeds reported status"
    sx={{ marginLeft: "auto", flexShrink: 0 }}
  />
);

const FederalCell = ({
  status,
  statusKind,
  isEndOfGroup,
  isStartOfGroup,
  isExceedance = false,
}: {
  status: RcraInfoFederalWasteGeneratorStatus | null;
  statusKind: GeneratorStatusKind;
  isEndOfGroup: boolean;
  isStartOfGroup: boolean;
  isExceedance?: boolean;
}) => {
  const { textColor, backgroundColor } = useMemo(
    () => getFederalGeneratorStatusColorSettings(status, statusKind),
    [status, statusKind]
  );

  return (
    <Box
      sx={{
        minHeight: 32,
        marginLeft: isStartOfGroup ? 0.25 : 0,
        marginRight: isEndOfGroup ? 0.25 : 0,
        backgroundColor,
        display: "flex",
        paddingX: 1,
        paddingY: 1,
        alignItems: "center",
        justifyContent: "flex-start",
        cursor: "pointer",
        transition: "all 0.2s ease",
        position: "relative",
        "&:hover": { opacity: 0.8, transform: "scale(1.02)" },
        zIndex: 3,
      }}
    >
      {isStartOfGroup && (
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.8125rem",
            color: textColor,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            // Allow the flex item to shrink below its content width so long
            // labels ellipsis instead of overflowing and pushing the exceedance
            // marker past the (overflow:hidden) month cell where it gets clipped.
            minWidth: 0,
          }}
        >
          {federalWasteGeneratorStatusToLabel(status ?? null)}
        </Typography>
      )}
      {isExceedance && <ExceedanceMarker />}
    </Box>
  );
};

/**
 * State-status cell. Borrows the federal color palette by tier
 * (see `stateColorEquivalent`) so it matches the federal bars, while the label
 * shows the true state status.
 */
const StateCell = ({
  status,
  statusKind,
  isStartOfGroup,
  isEndOfGroup,
  isExceedance = false,
}: {
  status: StateRegulatoryStatus | null;
  statusKind: GeneratorStatusKind;
  isStartOfGroup: boolean;
  isEndOfGroup: boolean;
  isExceedance?: boolean;
}) => {
  const { textColor, backgroundColor } = useMemo(
    () => getFederalGeneratorStatusColorSettings(stateColorEquivalent(status), statusKind),
    [status, statusKind]
  );

  return (
    <Box
      sx={{
        minHeight: 32,
        marginLeft: isStartOfGroup ? 0.25 : 0,
        marginRight: isEndOfGroup ? 0.25 : 0,
        backgroundColor,
        display: "flex",
        paddingX: 1,
        paddingY: 1,
        alignItems: "center",
        justifyContent: "flex-start",
        cursor: "pointer",
        transition: "all 0.2s ease",
        position: "relative",
        "&:hover": { opacity: 0.8, transform: "scale(1.02)" },
        zIndex: 3,
      }}
    >
      {isStartOfGroup && (
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.8125rem",
            color: textColor,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            // Allow the flex item to shrink below its content width so long
            // labels ellipsis instead of overflowing and pushing the exceedance
            // marker past the (overflow:hidden) month cell where it gets clipped.
            minWidth: 0,
          }}
        >
          {stateWasteGeneratorStatusToLabel(status ?? null)}
        </Typography>
      )}
      {isExceedance && <ExceedanceMarker />}
    </Box>
  );
};

type MonthRow = ReturnType<typeof buildStatusByMonth>[number];

type StatusBarRowProps = {
  label: string;
  months: MonthRow[];
  hoveredMonth?: string | null;
  onMonthHover?: (month: string) => void;
  onMonthLeave?: () => void;
  onMonthClick?: (month: string) => void;
  /** Overlay extends up past the row to meet the x-axis on the first (top) row. */
  overlayTop: number;
  overlayBottom: number;
  renderCell: (month: MonthRow) => ReactNode;
  mt?: number;
  mb?: number;
};

const StatusBarRow = ({
  label,
  months,
  hoveredMonth,
  onMonthHover,
  onMonthLeave,
  onMonthClick,
  overlayTop,
  overlayBottom,
  renderCell,
  mt,
  mb,
}: StatusBarRowProps) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", alignItems: "center", mt, mb }}>
      <Box
        sx={{
          width: STATUS_BAR_LABEL_WIDTH,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          pr: 1,
          minHeight: 32,
        }}
      >
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", textAlign: "right" }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flex: 1 }}>
        {months.map((monthData, index) => (
          <Box
            key={index}
            onMouseEnter={() => onMonthHover?.(monthData.month)}
            onMouseLeave={onMonthLeave}
            onClick={() => onMonthClick?.(monthData.month)}
            sx={{ position: "relative", flex: 1, overflow: "hidden" }}
          >
            {monthData.month === hoveredMonth && (
              <Box
                sx={{
                  position: "absolute",
                  top: overlayTop,
                  left: 0,
                  right: 0,
                  bottom: overlayBottom,
                  backgroundColor: theme.palette.grey[500],
                  opacity: 0.2,
                  zIndex: 1000,
                  pointerEvents: "none",
                }}
              />
            )}
            {renderCell(monthData)}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

function buildStatusByMonth(
  data: WasteShipmentChartsQuery["facilityWasteShipmentCharts"]["wasteShipmentsByMonth"]
) {
  return data.byMonth.map((monthData, index) => {
    const nextMonth = data.byMonth[index + 1];
    const prevMonth = data.byMonth[index - 1];
    const groupFlags = (
      pick: (m: (typeof data.byMonth)[number]) => unknown
    ): { startOfGroup: boolean; endOfGroup: boolean } => ({
      startOfGroup: !prevMonth || pick(prevMonth) !== pick(monthData),
      endOfGroup: !nextMonth || pick(nextMonth) !== pick(monthData),
    });
    const calc = groupFlags((m) => m.calculatedFederalGeneratorStatus);
    const reported = groupFlags((m) => m.reportedFederalGeneratorStatus);
    const calcState = groupFlags((m) => m.calculatedStateGeneratorStatus);
    const reportedState = groupFlags((m) => m.reportedStateGeneratorStatus);
    return {
      month: monthData.month,
      reportedFederalGeneratorStatus: monthData.reportedFederalGeneratorStatus,
      calculatedFederalGeneratorStatus: monthData.calculatedFederalGeneratorStatus,
      reportedStateGeneratorStatus: monthData.reportedStateGeneratorStatus,
      calculatedStateGeneratorStatus: monthData.calculatedStateGeneratorStatus,
      calculatedStartOfGroup: calc.startOfGroup,
      calculatedEndOfGroup: calc.endOfGroup,
      reportedStartOfGroup: reported.startOfGroup,
      reportedEndOfGroup: reported.endOfGroup,
      calculatedStateStartOfGroup: calcState.startOfGroup,
      calculatedStateEndOfGroup: calcState.endOfGroup,
      reportedStateStartOfGroup: reportedState.startOfGroup,
      reportedStateEndOfGroup: reportedState.endOfGroup,
      // Both exceedance flags are precomputed server-side, by the same code the
      // generator status tile reads, so the chart cannot disagree with it.
      federalExceedance: monthData.isOverReportedThreshold === true,
      stateExceedance: monthData.isOverReportedThresholdState === true,
    };
  });
}

type ClassificationBarsProps = {
  data: WasteShipmentChartsQuery["facilityWasteShipmentCharts"]["wasteShipmentsByMonth"];
  hoveredMonth?: string | null;
  onMonthHover?: (month: string) => void;
  onMonthLeave?: () => void;
  onMonthClick?: (month: string) => void;
  /** Show the state rows + label the federal rows explicitly (custom-state facilities). */
  showState?: boolean;
};

export const ClassificationBars = ({
  data,
  hoveredMonth,
  onMonthHover,
  onMonthLeave,
  onMonthClick,
  showState = false,
}: ClassificationBarsProps) => {
  const months = useMemo(() => buildStatusByMonth(data), [data]);

  const rowProps = { months, hoveredMonth, onMonthHover, onMonthLeave, onMonthClick };

  return (
    <Box mr={2.5} mb={2}>
      <StatusBarRow
        {...rowProps}
        label={showState ? "Federal reported" : "Reported"}
        overlayTop={-40}
        overlayBottom={0}
        mb={0.5}
        renderCell={(m) => (
          <FederalCell
            status={m.reportedFederalGeneratorStatus ?? null}
            statusKind={GeneratorStatusKind.REPORTED}
            isStartOfGroup={m.reportedStartOfGroup}
            isEndOfGroup={m.reportedEndOfGroup}
          />
        )}
      />
      <StatusBarRow
        {...rowProps}
        label={showState ? "Federal calculated" : "Calculated"}
        overlayTop={0}
        overlayBottom={showState ? 0 : -8}
        renderCell={(m) => (
          <FederalCell
            status={m.calculatedFederalGeneratorStatus ?? null}
            statusKind={GeneratorStatusKind.CALCULATED}
            isStartOfGroup={m.calculatedStartOfGroup}
            isEndOfGroup={m.calculatedEndOfGroup}
            isExceedance={m.federalExceedance}
          />
        )}
      />
      {showState && (
        <>
          <StatusBarRow
            {...rowProps}
            label="State reported"
            overlayTop={0}
            overlayBottom={0}
            mt={0.5}
            renderCell={(m) => (
              <StateCell
                status={m.reportedStateGeneratorStatus ?? null}
                statusKind={GeneratorStatusKind.REPORTED}
                isStartOfGroup={m.reportedStateStartOfGroup}
                isEndOfGroup={m.reportedStateEndOfGroup}
              />
            )}
          />
          <StatusBarRow
            {...rowProps}
            label="State calculated"
            overlayTop={0}
            overlayBottom={-8}
            renderCell={(m) => (
              <StateCell
                status={m.calculatedStateGeneratorStatus ?? null}
                statusKind={GeneratorStatusKind.CALCULATED}
                isStartOfGroup={m.calculatedStateStartOfGroup}
                isEndOfGroup={m.calculatedStateEndOfGroup}
                isExceedance={m.stateExceedance}
              />
            )}
          />
        </>
      )}
    </Box>
  );
};
